/* @flow */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import AppBar from 'material-ui/AppBar';
import IconButton from 'material-ui/IconButton';
import IconMenu from 'material-ui/IconMenu';
import MenuItem from 'material-ui/MenuItem';
import MoreVertIcon from 'material-ui/svg-icons/navigation/more-vert';
import MenuIcon from 'material-ui/svg-icons/navigation/menu';
import CloseIcon from 'material-ui/svg-icons/navigation/close';
import HomeIcon from 'material-ui/svg-icons/action/home';
import ArrowDropRight from 'material-ui/svg-icons/navigation-arrow-drop-right';
import type { ContextRouter } from 'react-router';

import Drawer from './Drawer';
import { logout } from '../../actions';

type Props = {
  authenticated: boolean,
  handleLogout: () => void,
} & ContextRouter;

type State = {
  open: boolean,
};

/* eslint-disable react/no-unused-prop-types */
type MenuItemProps = {
  key: string,
  text: string,
  menuItems?: mixed,
  leftIcon?: mixed,
  rightIcon?: mixed,
};
/* eslint-enable */

class Navbar extends Component<Props, State> {
  state = { open: false };

  handleToggle = () => this.setState({ open: !this.state.open });

  closeDrawer = () => this.setState({ open: false });

  handleMenuItem = key => {
    const navigate = location => {
      this.closeDrawer();
      this.props.history.push(location);
    };

    switch (key) {
      case 'close':
        this.closeDrawer();
        break;
      case 'home':
        navigate('/');
        break;
      case 'subject':
        navigate('/master/subject');
        break;
      case 'grade':
        navigate('/master/grade');
        break;
      case 'gradeGroup':
        navigate('/master/gradeGroup');
        break;
      case 'rate':
        navigate('/master/rate');
        break;
      case 'language':
        navigate('/master/language');
        break;
      case 'country':
        navigate('/master/country');
        break;
      case 'region':
        navigate('/master/region');
        break;
      case 'users':
        navigate('/admin/user');
        break;
      case 'students':
        navigate('/admin/student');
        break;
      case 'tutors':
        navigate('/admin/tutor');
        break;
      case 'activities':
        navigate('/admin/activities');
        break;
      case 'admin-push-notification':
        navigate('/admin/notification/push');
        break;
      case 'kitchensink':
        navigate('/kitchensink');
        break;
      case 'testing-system-notification':
        navigate('/testing/notification/system');
        break;
      case 'jobs':
        navigate('/admin/job');
        break;
      case 'tutoring-company':
        navigate('/admin/tutoring-company');
        break;
      case 'job-requests':
        navigate('/admin/job-request');
        break;
      case 'payment-export':
        navigate('/admin/finance/payment-export');
        break;
      case 'export':
        navigate('/admin/export');
        break;
      case 'testing-graphiql':
        navigate('/graphiql');
        break;
      default:
        break;
    }
  };

  handleLogin = () => {
    this.props.history.push('/login');
  };

  handleLogout = () => {
    this.props.handleLogout();
    this.props.history.replace('/login');
  };

  toggleDrawer = open => {
    this.setState({ open });
  };

  createItem = ({
    key,
    text,
    menuItems = null,
    leftIcon = null,
    rightIcon = null,
  }: MenuItemProps) => (
    <MenuItem
      key={key}
      leftIcon={leftIcon}
      rightIcon={rightIcon}
      menuItems={menuItems}
      onClick={() => {
        // $FlowFixMe
        this.handleMenuItem(key);
      }}
    >
      {text}
    </MenuItem>
  );

  createDrawerItems = authenticated => {
    const drawerItems = [
      this.createItem({ key: 'close', text: 'Close', leftIcon: <CloseIcon /> }),
      this.createItem({ key: 'home', text: 'Home', leftIcon: <HomeIcon /> }),
    ];

    if (authenticated) {
      drawerItems.push(
        this.createItem({
          key: 'master',
          text: 'Master Data',
          menuItems: [
            this.createItem({ key: 'subject', text: 'Subjects' }),
            this.createItem({ key: 'grade', text: 'Grades' }),
            this.createItem({ key: 'gradeGroup', text: 'Grade Groups' }),
            this.createItem({ key: 'region', text: 'Regions' }),
            this.createItem({ key: 'country', text: 'Countries' }),
            this.createItem({ key: 'language', text: 'Languages' }),
            this.createItem({ key: 'rate', text: 'Rates' }),
          ],
          rightIcon: <ArrowDropRight />,
        })
      );

      drawerItems.push(this.createItem({ key: 'users', text: 'Users' }));
      drawerItems.push(this.createItem({ key: 'students', text: 'Students' }));
      drawerItems.push(this.createItem({ key: 'tutors', text: 'Tutors' }));
      drawerItems.push(this.createItem({ key: 'export', text: 'Export' }));
      drawerItems.push(
        this.createItem({ key: 'activities', text: 'Activities' })
      );
      drawerItems.push(
        this.createItem({ key: 'job-requests', text: 'Job Requests' })
      );
      drawerItems.push(this.createItem({ key: 'jobs', text: 'Jobs' }));
      drawerItems.push(
        this.createItem({ key: 'tutoring-company', text: 'Companies' })
      );

      drawerItems.push(
        this.createItem({
          key: 'admin-push-notification',
          text: 'Push Notifications',
        })
      );
      drawerItems.push(
        this.createItem({ key: 'payment-export', text: 'Payment Export' })
      );

      drawerItems.push(
        this.createItem({
          key: 'testing-system-notification',
          text: 'System Notification',
        })
      );
      drawerItems.push(
        this.createItem({
          key: 'testing-graphiql',
          text: 'GraphiQL',
        })
      );
    }

    return drawerItems;
  };

  renderRightMenu = () => {
    const { authenticated } = this.props;

    let menuItems;

    if (authenticated) {
      menuItems = <MenuItem onClick={this.handleLogout} primaryText="Logout" />;
    } else {
      menuItems = <MenuItem onClick={this.handleLogin} primaryText="Login" />;
    }

    return (
      <IconMenu
        iconButtonElement={
          <IconButton>
            <MoreVertIcon />
          </IconButton>
        }
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        targetOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        {menuItems}
      </IconMenu>
    );
  };

  render() {
    const { authenticated } = this.props;
    const { open } = this.state;

    const drawerItems = this.createDrawerItems(authenticated);

    return (
      <div>
        <AppBar
          title="Cognition"
          iconElementLeft={
            <IconButton onClick={this.handleToggle}>
              <MenuIcon />
            </IconButton>
          }
          iconElementRight={this.renderRightMenu()}
        />
        <Drawer
          drawerItems={drawerItems}
          toggleDrawer={this.toggleDrawer}
          open={open}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  authenticated: state.auth.authenticated,
  username: state.auth.username,
});

const mapDispatchToProps = dispatch => ({
  handleLogout: () => {
    dispatch(logout());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Navbar);



// WEBPACK FOOTER //
// ./src/components/navbar/Navbar.js